import { Flex, GridItem } from "@chakra-ui/react";
import {
  MotionBox as Box,
  MotionGrid as Grid,
  staggeredAppear,
  viewAppearDefault,
  MotionImage,
} from "utils/motion";
import Trans, { useFormatMessage } from "components/base/Trans";
import LanguageSelector from "components/blocks/LanguageSelector";
import FooterNote from "components/sections/FooterNote";
import Text from "components/base/Text";
import Container from "components/base/Container";
import Link from "components/base/Link";
import navbarContent from "utils/navbar.json";

const FooterLinkMap = ({ small }) => {
  const formatMessage = useFormatMessage();
  return (
    <Box px={[6, null, 20]} position="relative">
      {!small && <Box bg="gray.800" h="1px" m="auto" maxWidth="xl" />}
      <Flex
        m="auto"
        position={["absolute", null, null, null, "relative"]}
        right={[4, 4, 20, 20, 0]}
        maxWidth="xl"
        py={6}
        justifyContent="flex-end"
        alignItems="center"
      >
        <Box
          variants={viewAppearDefault}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
        >
          <LanguageSelector />
        </Box>
      </Flex>
      <Container pt="1px" px={0} m="auto" pb="md">
        <Grid
          variants={staggeredAppear}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          width="100%"
          templateColumns={["1fr", null, null, null, "0.75fr auto 1fr auto"]}
          alignItems="start"
          gap={[4, 4, 6]}
        >
          <Link
            href="/"
            _focus={{ boxShadow: "0 0 0" }}
            width="59px"
            pt={[6, 6, 6, 6, 0]}
          >
            <MotionImage
              variants={viewAppearDefault}
              alt="logo footer"
              src="/images/logo-hill-footer.svg"
              width="59px"
              height="100px"
            />
          </Link>
          <Grid
            variants={viewAppearDefault}
            templateRows={["repeat(10, 1fr)", null, null, "repeat(1, 1fr)"]}
            templateColumns="repeat(10, 1fr)"
            gap={[4, 4, 4, 4, 12]}
            mt={["64px", null, null, null, 0]}
            mb="64px"
          >
            {navbarContent.map((e, idx) => (
              <GridItem
                rowSpan={
                  idx === 0
                    ? [10, 10, 10, 1]
                    : idx === 1
                      ? [2, 2, 2, 1]
                      : [8, 8, 8, 1]
                }
                colSpan={
                  idx === 0
                    ? [6, 6, 5, 2]
                    : idx === 1
                      ? [4, 4, 5, 2]
                      : [4, 4, 5, 2]
                }
                key={e.label}
              >
                {e.url ? (
                  <Link
                    key={e.id}
                    href={e.url}
                    _hover={{
                      textDecoration: "none",
                      opacity: e.url ? 0.8 : 1,
                    }}
                  >
                    <Text type="title_xs" color="primary" mb="sm">
                      <Trans id={e.label} />
                    </Text>
                  </Link>
                ) : (
                  <Text type="title_xs" color="primary" mb="sm">
                    <Trans id={e.label} />
                  </Text>
                )}
                {e.items.map((item) => (
                  <Link
                    key={item.id}
                    href={item.url}
                    _hover={{ textDecoration: "none", opacity: 0.8 }}
                    _focus={{ boxShadow: "0 0 0" }}
                  >
                    <Text type="p_sm" mt="xs" maxW="180px">
                      <Trans id={item.label} />
                    </Text>
                  </Link>
                ))}
                {e.subItems?.map((item) => (
                  <Link
                    key={item.id}
                    href={item.url}
                    _hover={{ textDecoration: "none", opacity: 0.8 }}
                    _focus={{ boxShadow: "0 0 0" }}
                  >
                    <Text type="p_sm" mt="xs">
                      <Trans id={item.label} />
                    </Text>
                  </Link>
                ))}
              </GridItem>
            ))}
            <GridItem
              colSpan={[5, 5, 5, 2]}
              variants={viewAppearDefault}
              flexDirection="column"
              alignItems="start"
              mt={[4, null, null, 0]}
              display={["none", "none", "flex"]}
            >
              <Text type="title_xs" color="primary">
                <Trans id="LBL_FOOTER_ADDRESS_TITLE" />
              </Text>
              <Text type="p_sm" lineHeight={1.71} mt="sm">
                <Trans id="LBL_CONTACT_ADDRESS" />
              </Text>
            </GridItem>
            <GridItem
              colSpan={[6, 6, 5, 2]}
              variants={viewAppearDefault}
              display="flex"
              flexDirection="column"
              alignItems="start"
              mt={[4, null, 4, 0]}
            >
              <Text type="title_xs" color="primary" mb={2}>
                <Trans id="LBL_MENU_CONTACT" />
              </Text>
              <Text
                type="title_xs"
                mb="2px"
                textTransform="none"
                letterSpacing={0}
              >
                <Trans id="LBL_CONTACT_PHONE_GENERAL_LABEL" />
              </Text>
              <Link
                href={`tel:${process.env.phone_general}`}
                mb={2}
                color="white"
              >
                <Text type="p_sm">{process.env.phone_general}</Text>
              </Link>
              <Text
                type="title_xs"
                mb="2px"
                textTransform="none"
                letterSpacing={0}
              >
                <Trans id="LBL_CONTACT_PHONE_ACCOUNTS_LABEL" />
              </Text>
              <Link
                href={`tel:${process.env.phone_accounts}`}
                mb={2}
                color="white"
              >
                <Text type="p_sm">{process.env.phone_accounts}</Text>
              </Link>
              <Text
                type="title_xs"
                mb="2px"
                textTransform="none"
                letterSpacing={0}
              >
                <Trans id="LBL_CONTACT_PHONE_SALES_LABEL" />
              </Text>
              <Link
                href={`tel:${process.env.phone_sales}`}
                mb={2}
                color="white"
              >
                <Text type="p_sm">{process.env.phone_sales}</Text>
              </Link>
              <Link href={formatMessage({ id: "LBL_CONTACT_MAIL_LINK" })}>
                <Text style={{ textDecoration: "underline" }} fontSize="xs">
                  <Trans id="LBL_CONTACT_MAIL_LABEL" />
                </Text>
              </Link>
            </GridItem>
            <GridItem
              colSpan={4}
              variants={viewAppearDefault}
              flexDirection="column"
              alignItems="start"
              mt={4}
              display={["flex", "flex", "none"]}
            >
              <Text type="title_xs" color="primary">
                <Trans id="LBL_FOOTER_ADDRESS_TITLE" />
              </Text>
              <Text
                type="p_sm"
                lineHeight={1.71}
                mt="2"
                fontSize={["11px", "12px", "14px"]}
              >
                <Trans id="LBL_CONTACT_ADDRESS" />
              </Text>
            </GridItem>
          </Grid>
        </Grid>
        <Box bg="gray.800" h="1px" w="100%" />
        <FooterNote />
      </Container>
    </Box>
  );
};

export default FooterLinkMap;
